import { Injectable } from '@angular/core';

import { Mote, Media, Coords } from '../models';
import { HttpClient } from '@angular/common/http';

@Injectable({ providedIn: 'root' })
export class WikiService {
    private readonly WIKI_URL = "https://www.wikidata.org/w/api.php";

    constructor(private http: HttpClient) {
        // Constructor
    }

    public getWikiEntity(entityId): Promise<any | null> {
        // Url to get full entity
        let wikiUrl = `${this.WIKI_URL}?action=wbgetentities&ids=${entityId}&format=json&origin=*`;
        //let wikiUrl = `${WIKI_URL}?action=wbgetclaims&entity=${entityId}&property=P373&format=json&origin=*`;
        return new Promise((resolve, reject) => {
            // Get wiki place data
            this.http.get(wikiUrl).toPromise().then((res: any) => {
                let json = res.json().entities;
                console.log("Wiki entity result ", json[entityId]);
                resolve(json[entityId]);
            }).catch(err => reject(err));
        })
    }

    private getWikiEntryName(entityJson): string {
        // Get the wiki page section of entity
        let data = entityJson && entityJson.claims ? entityJson.claims.P373 : [];
        if(!data) data = entityJson && entityJson.claims ? entityJson.claims : [];
        if(data.length <= 0) return "";
        let entry = data[0];
        // Get the wiki entry name from record
        if(!entry || !entry.mainsnak || !entry.mainsnak.datavalue) return "";
        return entry.mainsnak.datavalue.value;
    }

    private getWikiLocation(entityJson): Coords | null {

        // Get the wiki page section of entity
        let data = entityJson && entityJson.claims ? entityJson.claims.P625 : [];
        if(!data) data = entityJson && entityJson.claims ? entityJson.claims : [];
        if(data.length <= 0) return null;

        let entry = data[0];
        // Get the wiki entry name from record
        if(!entry || !entry.mainsnak || !entry.mainsnak.datavalue) return null;
        let wikiPos = entry.mainsnak.datavalue.value;
        return new Coords(wikiPos.latitude, wikiPos.longitude, true);
    }

    /**
     * Get wiki pages / images close to the given coordinates
     */
    public async getNearbyWikiPages(coord: Coords) {
        const ggslimit = 5;
        const radius = 200;
        const imageWidth = 720;
        const req = `${this.WIKI_URL}?action=query&format=json&origin=*&formatversion=2&prop=coordinates|pageprops|pageimages|description|info|pageterms&inprop=url&colimit=10&generator=geosearch&ggsradius=${radius}&ggsnamespace=0&ggslimit=${ggslimit}&redirects=no&uselang=en&ppprop=displaytitle&piprop=thumbnail&pithumbsize=${imageWidth}&pilimit=50&codistancefrompoint=${coord.lat}|${coord.long}&ggscoord=${coord.lat}|${coord.long}`;
         // Get wiki place data
         const rsp = await this.http.get<{ query: any }>(req).toPromise().catch(err => { console.log(err); return null });
         return rsp?.query;
    }

    /**
     * Get a wiki entry from a wiki entry id (ie. provided by mapbox POI search result)
     * @param entityId Id of article / wiki POI
     */
    public async getWikiEntityAsMote(entityId: string): Promise<Mote | null> {
        return new Promise(async (resolve, reject) => {
            let mote = new Mote("wiki-" + entityId);

            let entity = await this.getWikiEntity(entityId);
            // Get entry name from response
            let entryName = this.getWikiEntryName(entity);
            if(!entryName) return reject(null);

            // Request wiki page data
            let pages = await this.getWikiData(entryName);
            mote.setPosition(this.getWikiLocation(entity));
            console.log("Position: ", mote.position);

            // Extract title and description from rsp
            mote.title = pages.length > 0 ? pages[0].title : "No Title";
            let preview = (entity.descriptions && entity.descriptions['en']) ? entity.descriptions['en'].value : "";
            mote.previewText = preview.replace(/^\w/, c => c.toUpperCase());

            mote.text = pages.length > 0 ? pages[0].extract : "No wiki page found";
            mote.project = "wiki";
            mote.createdBy = "wiki_robot";

            // Get wiki image
            let media = new Media("wiki-image1-" + entityId);

            media.thumb.url = await this.getWikiImageUrl(entityId, 150);
            media.hd.url = await this.getWikiImageUrl(entityId, 1024);
            mote.mediaArray.push(media);
            mote.image = media;
            resolve(mote);
        });
    }

    public getWikiData(title): Promise<any | null> {
        const WIKI_URL = "https://en.wikipedia.org/w/api.php?";
        let wikiUrl = `${WIKI_URL}format=json&action=query&prop=extracts&exintro&explaintext&redirects=1&titles=${title}&origin=*`;

        return new Promise((resolve, reject) => {
            // Get wiki place data
            this.http.get<{ query: any }>(wikiUrl).toPromise().then((json) => {
                let data = json.query;
                resolve(data && data.pages ? Object.keys(data.pages).map(k => data.pages[k]) : []);
            }).catch(err => reject(err));
        });
    }

    public getWikiImageUrl(entityId, width = 200): Promise<string> {
        const WIKI_URL = "https://www.wikidata.org/w/api.php";
        let wikiImageBase = `https://commons.wikimedia.org/w/thumb.php?width=${width}&f=`;

        let wikiImageUrl = `${WIKI_URL}?action=wbgetclaims&entity=${entityId}&property=P18&format=json&origin=*`;

        return new Promise((resolve, reject) => {
            // Get wiki place data
            this.http.get<{ claims: any }>(wikiImageUrl).toPromise().then((json) => {
                let data = json.claims ? json.claims.P18 : [];
                console.log("Image data: ", json);

                resolve(data && data.length > 0 ? wikiImageBase + encodeURI(data[0].mainsnak.datavalue.value) : "");
            }).catch(err => reject(err));
        });
    }
}
